import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { View, Keyboard, ScrollView } from 'react-native';
import moment from 'moment';

import { useDialogContext, useUserContext } from '../../../common/utils/context';
import { MEASUREMENT_UNIT_SHORT_NAME } from '../../../common/constants';
import { getAdjustedExpiryDatesByQuantityChange, updateExpiryDates } from '../../../common/utils/planogramItem';
import { formatExpiryDatesForDisplay } from '../../../common/utils/pickList/expiryDateHelpers';
import { deepEqual } from '../../../common/utils/utils';

import { InputParlevelQuantity } from '../../components/input';
import { Text, ErrorCallout } from '../../components';
import { PrimaryButton, SecondaryButton, StatusBadge } from '../../design/components';
import { useThemedStyles } from '../../hooks/useThemedStyles';


import PlanogramItemSalesLastXDays from '../planogram/PlanogramItemSalesLastXDays';
import PlanogramItemExpiryDatePicker from '../planogram/PlanogramItemExpiryDatePicker';
import { batchSameProducts } from '../../../common/utils/planograms/planogram';

const PickListItemEdit = ({
	pickListItems, // for real items are PlanogramItems type
	onButtonPress,
	onSaveExpiryDates,
	quantityFieldName,
	extraQuantityFieldName,
	isMarket,
	allowExpiryDateEditing,
	defaultShowSalesLastXDaysForPicklists,
}) => {

	const styles = useThemedStyles(createStyles);
	const [isLoading, setIsLoading] = useState(false);
	const [saveError, setSaveError] = useState(null);
	const [isSavingExpiryDates, setIsSavingExpiryDates] = useState(false);
	const { tenantSettings } = useUserContext();
	const { planogramItemDaysSinceLastSale } = tenantSettings;
	const { closeDialog } = useDialogContext();
	// resolve required values

	// when products are batched we pass pickListItems, so we need to sum up quantities, salesLastXDays
	// and other values to display them in dialog
	const batchedPicklistItemsByProduct = useMemo(() => {
		return batchSameProducts(pickListItems, quantityFieldName, extraQuantityFieldName);
	}, [pickListItems, quantityFieldName, extraQuantityFieldName]);

	const {
		id: pickListItemsIds,
		requiredQuantity,
		parlevel: planogramItemParlevel,
		quantity: planogramItemQuantity,
		selections,
		isPicked,
		planogramItemIsIngredient,
		batchedSalesLast7Days,
		batchedSalesLast14Days,
		batchedSalesLast30Days,
		batchedSalesLast365Days,
		batchedLastProductChangeTime,
		batchedPlanogramItemCreated,
		inactive,
		product,
	} = batchedPicklistItemsByProduct?.[0] || {};
	const [selectedPickListItem = null] = pickListItems || [];
	const pickedQuantity = useMemo(() => {
		const explicitPickedQuantity = (Number(selectedPickListItem?.pickedQuantity) || 0) +
			(Number(selectedPickListItem?.pickedExtraQuantity) || 0);
		if (explicitPickedQuantity > 0) {
			return explicitPickedQuantity;
		}

		const hasPickedFlag = Boolean(selectedPickListItem?.isPicked || selectedPickListItem?.extraQuantityIsPicked || isPicked);
		return hasPickedFlag ? Math.max(0, Number(requiredQuantity) || 0) : 0;
	}, [
		selectedPickListItem?.pickedQuantity,
		selectedPickListItem?.pickedExtraQuantity,
		selectedPickListItem?.isPicked,
		selectedPickListItem?.extraQuantityIsPicked,
		isPicked,
		requiredQuantity,
	]);

	const hasPickedQuantity = pickedQuantity > 0 || (pickListItems || []).some(item => item.isPicked || item.extraQuantityIsPicked);
	const resolvedPlanogramItemQuantity = useMemo(() => {
		if (pickedQuantity > 0) {
			return pickedQuantity;
		}

		const postServiceQuantity = selectedPickListItem?.planogramItemQuantityPostInventoryChange ??
			selectedPickListItem?.planogramItemQuantityPostService ??
			selectedPickListItem?.planogramItemQuantity ??
			planogramItemQuantity ??
			0;
		const projectedQuantity = (Number(planogramItemQuantity) || 0) + (Number(requiredQuantity) || 0);
		const suggestedQuantity = Number(selectedPickListItem?.suggestedExpiryDatesFromWarehouse?.quantityRequested) || 0;

		return Math.max(
			0,
			Number(postServiceQuantity) || 0,
			projectedQuantity,
			suggestedQuantity,
		);
	}, [
		pickedQuantity,
		selectedPickListItem?.planogramItemQuantityPostInventoryChange,
		selectedPickListItem?.planogramItemQuantityPostService,
		selectedPickListItem?.planogramItemQuantity,
		selectedPickListItem?.suggestedExpiryDatesFromWarehouse?.quantityRequested,
		planogramItemQuantity,
		requiredQuantity,
	]);

	const isBatchedSelection = (pickListItems || []).length > 1;
	const canShowExpiryDates = allowExpiryDateEditing &&
		!planogramItemIsIngredient &&
		selectedPickListItem?.planogramItemId &&
		resolvedPlanogramItemQuantity > 0 &&
		!isBatchedSelection;
	const canEditQuantity = !hasPickedQuantity;
	const canEditExpiryDates = canShowExpiryDates && hasPickedQuantity;
	const useSingleExpiryDateMode = isMarket && !hasPickedQuantity;

	const [newQuantity, setNewQuantity] = useState(requiredQuantity);
	const [expiryDateItemKey, setExpiryDateItemKey] = useState(null);
	const [expiryDateSelectorDate, setExpiryDateSelectorDate] = useState(moment());
	const suggestedExpiryDatesFromWarehouse = selectedPickListItem?.suggestedExpiryDatesFromWarehouse;
	const resolvedCurrentExpiryDates = useMemo(() => {
		const hasPickedQuantityInSnapshot = (
			Number(selectedPickListItem?.pickedQuantity) || 0
		) + (
			Number(selectedPickListItem?.pickedExtraQuantity) || 0
		) > 0 || selectedPickListItem?.isPicked || selectedPickListItem?.extraQuantityIsPicked;
		const sourceExpiryDates = hasPickedQuantityInSnapshot
			? (selectedPickListItem?.planogramItemExpiryDatesAtPick || {})
			: {};

		return getAdjustedExpiryDatesByQuantityChange(
			sourceExpiryDates,
			resolvedPlanogramItemQuantity,
			useSingleExpiryDateMode,
		);
	}, [
		selectedPickListItem?.planogramItemExpiryDatesAtPick,
		selectedPickListItem?.pickedQuantity,
		selectedPickListItem?.pickedExtraQuantity,
		selectedPickListItem?.isPicked,
		selectedPickListItem?.extraQuantityIsPicked,
		resolvedPlanogramItemQuantity,
		useSingleExpiryDateMode,
	]);
	const [draftExpiryDates, setDraftExpiryDates] = useState(resolvedCurrentExpiryDates);

	useEffect(() => {
		setDraftExpiryDates(resolvedCurrentExpiryDates);
	}, [resolvedCurrentExpiryDates]);

	const hasPendingExpiryChanges = useMemo(() => {
		return !deepEqual(draftExpiryDates || {}, resolvedCurrentExpiryDates || {});
	}, [draftExpiryDates, resolvedCurrentExpiryDates]);

	const saveExpiryDatesIfNeeded = useCallback(async () => {
		if (!hasPendingExpiryChanges || !canEditExpiryDates || !onSaveExpiryDates || !selectedPickListItem) {
			return;
		}

		setIsSavingExpiryDates(true);
		try {
			await onSaveExpiryDates({
				pickListItemIds: [selectedPickListItem.id],
				expiryDates: draftExpiryDates,
			});
		}
		finally {
			setIsSavingExpiryDates(false);
		}
	}, [canEditExpiryDates, draftExpiryDates, hasPendingExpiryChanges, onSaveExpiryDates, selectedPickListItem]);

	const confirmValueChange = async valueOverride => {
		const resolvedQuantity = valueOverride === undefined ? Number(newQuantity) : valueOverride;
		setIsLoading(true);
		setSaveError(null);
		Keyboard.dismiss();
		try {
			await saveExpiryDatesIfNeeded();
			if (canEditQuantity && onButtonPress) {
				await onButtonPress(pickListItemsIds, resolvedQuantity);
			}
			closeDialog();
		}
		catch (error) {
			setSaveError(error?.message || 'Unable to save this item. Check your connection and try again.');
		}
		finally {
			setIsLoading(false);
		}
	};

	const onClickButton = newValue => async () => {
		await confirmValueChange(newValue);
	};

	const setPlanogramItemValue = useCallback((_, field) => (value) => {
		if (field !== 'expiryDates' || !value?.selection) {
			return;
		}

		setDraftExpiryDates(currentExpiryDates => {
			return updateExpiryDates(
				currentExpiryDates || {},
				value,
				resolvedPlanogramItemQuantity,
				useSingleExpiryDateMode,
			);
		});
	}, [resolvedPlanogramItemQuantity, useSingleExpiryDateMode]);

	const expiryDateButtons = useMemo(() => {
		const buttonCount = resolvedPlanogramItemQuantity && !useSingleExpiryDateMode
			? resolvedPlanogramItemQuantity
			: 1;
		const buttons = [];

		for (let i = 1; i <= buttonCount; i += 1) {
			const value = draftExpiryDates?.[i];
			buttons.push({
				key: i,
				label: value ? moment(value).format('MMM D') : 'Unknown',
				onPress: () => {
					setExpiryDateItemKey(`${i}`);
					setExpiryDateSelectorDate(value ? moment(value) : moment());
				},
			});
		}

		return buttons;
	}, [draftExpiryDates, useSingleExpiryDateMode, resolvedPlanogramItemQuantity]);

	const warehouseExpiryPreviewText = useMemo(() => {
		const suggestedQuantity = Number(suggestedExpiryDatesFromWarehouse?.quantityRequested) || 0;
		if (!suggestedQuantity) {
			return null;
		}

		const dates = formatExpiryDatesForDisplay(
			suggestedExpiryDatesFromWarehouse?.expiryDates || {},
			suggestedQuantity,
			false,
		);
		if (!dates.length) {
			return null;
		}

		return dates.join(', ');
	}, [suggestedExpiryDatesFromWarehouse]);

	const expiryPreviewText = useMemo(() => {
		if (!canShowExpiryDates || !resolvedPlanogramItemQuantity) {
			return 'No quantity in machine.';
		}
		if (!hasPickedQuantity) {
			if (warehouseExpiryPreviewText) {
				return `Expected from warehouse: ${warehouseExpiryPreviewText}`;
			}
			return 'Captured after this item is picked.';
		}

		const dates = formatExpiryDatesForDisplay(
			draftExpiryDates || {},
			resolvedPlanogramItemQuantity,
			useSingleExpiryDateMode,
		);
		if (!dates.length) {
			return 'Expiry unknown.';
		}
		return dates.join(', ');
	}, [canShowExpiryDates, draftExpiryDates, hasPickedQuantity, useSingleExpiryDateMode, resolvedPlanogramItemQuantity, warehouseExpiryPreviewText]);

	return (
		<View style={styles.container}>
			<Text style={styles.title}>
				{ // hide selections if market
					isMarket ? '' : `${selections}. `
				}
				{ product && product.name }
			</Text>
			<View style={styles.field}>
				<Text style={styles.label}>Quantity</Text>
				<View style={styles.quantityRow}>
					<InputParlevelQuantity
						product={product}
						suffix={product && MEASUREMENT_UNIT_SHORT_NAME[product.measurementUnit]}
						value={requiredQuantity}
						onChangeInternal={setNewQuantity}
						disabled={isLoading || isSavingExpiryDates || !canEditQuantity}
						autoFocus={canEditQuantity}
					/>
					<SecondaryButton
						style={styles.flexButton}
						label="Zero"
						disabled={isLoading || isSavingExpiryDates || !canEditQuantity}
						onPress={onClickButton(0)}
					/>
					<SecondaryButton
						style={styles.flexButton}
						label="Full"
						disabled={isLoading || isSavingExpiryDates || !canEditQuantity}
						onPress={onClickButton(planogramItemParlevel - planogramItemQuantity)}
					/>
				</View>
			</View>

			{hasPickedQuantity && <Text style={styles.note}>This item is picked. Untick it before changing its quantity.</Text>}
			{saveError && <ErrorCallout text={saveError} />}

			{
				allowExpiryDateEditing && (
					<View style={styles.field}>
						<Text style={styles.label}>
							{hasPickedQuantity ? 'Picked warehouse expiry dates' : 'Expiry dates'}
						</Text>
						{
							isBatchedSelection && (
								<Text style={styles.note}>
									Edit expiry dates per selection (disable batch same product in settings).
								</Text>
							)
						}
						{
							!isBatchedSelection && (
								<>
									{
										!canEditExpiryDates && (
											<Text style={styles.note}>
												Current: {expiryPreviewText}
											</Text>
										)
									}
									{
										canEditExpiryDates ? (
											<>
												<ScrollView
													horizontal={true}
													contentContainerStyle={styles.expiryButtons}
													keyboardShouldPersistTaps="handled"
												>
													{expiryDateButtons.map(button => (
														<SecondaryButton
															key={button.key}
															label={button.label}
															disabled={isSavingExpiryDates || isLoading}
															onPress={button.onPress}
														/>
													))}
												</ScrollView>
												{
													hasPendingExpiryChanges && (
														<Text style={styles.note}>
															{
																canEditQuantity
																	? 'Expiry date changes will be saved when you tap OK, Zero, or Full.'
																	: 'Expiry date changes will be saved when you tap Save.'
															}
														</Text>
													)
												}
											</>
										) : (
											<Text style={styles.note}>
												{hasPickedQuantity ? 'No editable expiry dates for this item.' : 'Expiry dates can be edited after this item is picked.'}
											</Text>
										)
									}
								</>
							)
						}
					</View>
				)
			}

			{
				((!planogramItemIsIngredient && defaultShowSalesLastXDaysForPicklists) || isMarket) && (
					<View style={styles.infoRow}>
						{
							!planogramItemIsIngredient && defaultShowSalesLastXDaysForPicklists && (
								<View style={styles.field}>
									<Text style={styles.label}>Sales last {planogramItemDaysSinceLastSale} days</Text>
									<PlanogramItemSalesLastXDays
										salesLast365Days={batchedSalesLast365Days}
										salesLast30Days={batchedSalesLast30Days}
										salesLast14Days={batchedSalesLast14Days}
										salesLast7Days={batchedSalesLast7Days}
										lastProductChangeTime={batchedLastProductChangeTime}
										created={batchedPlanogramItemCreated}
										dialogStyle={true}
									/>
								</View>
							)
						}
						{
							isMarket && (
								<View style={styles.field}>
									<Text style={styles.label}>Inactive</Text>
									<StatusBadge
										label={inactive ? 'True' : 'False'}
										tone={inactive ? 'success' : 'danger'}
									/>
								</View>
							)
						}
					</View>
				)
			}

			<PrimaryButton
				size="compact"
				label={canEditQuantity ? 'OK' : (canEditExpiryDates ? 'Save' : 'Close')}
				loading={isLoading || isSavingExpiryDates}
				disabled={isLoading || isSavingExpiryDates}
				onPress={() => confirmValueChange()} // don't change as it will pass event
			/>

			{
				canEditExpiryDates && Boolean(expiryDateItemKey) && (
					<PlanogramItemExpiryDatePicker
						planogramItem={{ id: selectedPickListItem.planogramItemId }}
						setPlanogramItemValue={setPlanogramItemValue}
						expiryDateItemKey={expiryDateItemKey}
						setExpiryDateItemKey={setExpiryDateItemKey}
						setExpiryDateSelectorDate={setExpiryDateSelectorDate}
						expiryDateSelectorDate={expiryDateSelectorDate}
					/>
				)
			}
		</View>
	);
};

const createStyles = ({ ui: { colors, type, space } }) => ({
	container: {
		gap: space.md,
	},
	title: {
		...type.rowTitleSemibold,
		color: colors.text,
		// Keep clear of the dialog's close button.
		paddingRight: 36,
	},
	field: {
		gap: space.xs,
	},
	label: {
		...type.caption,
		color: colors.textSecondary,
	},
	note: {
		...type.secondary,
		color: colors.textSecondary,
	},
	quantityRow: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: 10,
	},
	flexButton: {
		flex: 1,
	},
	expiryButtons: {
		flexDirection: 'row',
		gap: 6,
	},
	infoRow: {
		flexDirection: 'row',
		alignItems: 'flex-start',
		gap: space.lg,
	},
});


export default React.memo(PickListItemEdit);
